import { useGetUserInfoQuery } from '@/api/endpoints/users';
import { GlobalFilterKeys } from '@/api/types';
import { useAppSelector } from '@/app/hooks';
import {
  FilterHierarchy,
  globalFilterChangePendingSelector,
  isAdvertiserBasedHierarchy,
  Platform,
  PLATFORM_FILTER_HIERARCHY,
} from '@/features/users';

interface UseGlobalFilterKeysResult {
  globalFilterKeys: GlobalFilterKeys;
  platform: Platform['name'] | null;
  isTradeDesk: boolean;
  isMeta: boolean;
  isDV360: boolean;
  filterHierarchy: FilterHierarchy | null;
  isGlobalFilterReady: boolean;
}

/**
 * A custom hook that derives cache keys from the user's active global filters
 * (platform, partner, advertiser, account).
 *
 * These keys are passed as arguments to RTK Query endpoints to create unique
 * cache entries per filter combination, preventing stale data race conditions
 * that occur when filters change while requests are in-flight.
 *
 * Note: Filter keys are NOT sent to the backend — the backend reads filter
 * state from the DB. They are used purely for RTK Query cache keying.
 *
 * Filter hierarchy:
 * - Platform (all)
 *   - Partner        (partner-advertiser hierarchy)
 *     - Advertiser   (partner-advertiser hierarchy, requires Partner)
 *   - Advertiser     (advertiser hierarchy, no Partner)
 *   - Account        (account hierarchy)
 *
 * @returns globalFilterKeys     - Cache key values derived from active filters
 * @returns platform             - Active platform name
 * @returns isTradeDesk          - Whether the active platform is The Trade Desk
 * @returns isMeta               - Whether the active platform is Meta
 * @returns isDV360              - Whether the active platform is DV360
 * @returns filterHierarchy      - The filter hierarchy type for the active platform
 * @returns isGlobalFilterReady  - False while `getUserInfo` is fetching or a filter-change mutation
 *                                 is in-flight. Use as the `skip` condition for dependent queries
 *                                 to ensure they fire exactly once with the final search params.
 */
const useGlobalFilterKeys = (): UseGlobalFilterKeysResult => {
  const { data: userInfo, isFetching } = useGetUserInfoQuery(null);
  const filterChangePending = useAppSelector(globalFilterChangePendingSelector);

  const platformId = userInfo?.selectedPlatform?.id ?? null;
  const platform = userInfo?.selectedPlatform?.name ?? null;
  const isTradeDesk = platform === 'The Trade Desk';
  const isMeta = platform === 'Meta';
  const isDV360 = platform === 'DV360';

  const filterHierarchy = platform ? PLATFORM_FILTER_HIERARCHY[platform] : null;
  const hasPartnerFilter = filterHierarchy === 'partner-advertiser';
  const hasAdvertiserFilter = platform
    ? isAdvertiserBasedHierarchy(platform)
    : false;
  const hasAccountHierarchy = filterHierarchy === 'account';

  const partnerId = hasPartnerFilter
    ? (userInfo?.selectedPartner?.id ?? null)
    : null;
  const advertiserId = hasAdvertiserFilter
    ? (userInfo?.selectedAdvertiser?.id ?? null)
    : null;
  const accountId = hasAccountHierarchy
    ? (userInfo?.selectedAccount?.id ?? null)
    : null;

  return {
    globalFilterKeys: {
      platformId,

      partnerId,
      advertiserId,

      accountId,
    },

    platform,
    isTradeDesk,
    isMeta,
    isDV360,
    filterHierarchy,
    isGlobalFilterReady: !isFetching && !filterChangePending,
  };
};

export default useGlobalFilterKeys;
